import styles from './styles.scss';
import { Button } from 'components/Button';
import { NO_ELEMENT, rm } from 'utils';
import { useEffect, useReducer } from 'react';
import Input from 'components/Input/Input';
import * as rules from 'components/Input/validation';
import { SuggestedAddress } from 'models/SuggestedAddress';
import { AddressSuggestion } from '../AddressSuggestion/AddressSuggestion';
import { useToggle } from 'hooks';
import { postValidateAddress } from 'services/ApiCheckoutPageService';
import {
  Action,
  ChangeAddressProps,
  DisplayAddressProps,
  OwnProps,
  initialState,
  InputType,
  ownReducer,
  validate
} from './AddressHelper';

const inputChange =
  (dispatch: (value: Action) => void, input: InputType) =>
  (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({
      type: input,
      value: {
        value: e.target.value,
        error: rules[input](e.target.value)
      }
    });
  };

const ChangeAddress = ({
  state,
  toggle,
  dispatch,
  reset,
  onChange,
  validationError
}: ChangeAddressProps) => {
  const [streetSuggestionsVisible, toggleStreetSuggestions] = useToggle(false);
  const [zipCodeSuggestionsVisible, toggleZipCodeSuggestions] = useToggle(false);

  const toggleInternal = () => {
    if (validate(state, dispatch)) {
      void postValidateAddress(state.street.value, state.zipCode.value, state.city.value).then(
        response => {
          if (response.isValid) {
            onChange(
              {
                name: state.name.value,
                phone: state.phone.value,
                street: state.street.value,
                zipCode: state.zipCode.value,
                city: state.city.value
              },
              state.email.value
            );
            toggle();
          } else {
            validationError();
          }
        }
      );
    }
  };

  const selectAddressSuggestion = (suggestion: SuggestedAddress) => {
    const street = `${suggestion.street ?? ''} ${suggestion.streetNumber ?? ''}${
      suggestion.letter ?? ''
    }`;
    dispatch({
      type: 'street',
      value: {
        value: street,
        error: rules['street'](street)
      }
    });
    dispatch({
      type: 'zipCode',
      value: {
        value: suggestion.postalcode,
        error: rules['zipCode'](suggestion.postalcode)
      }
    });
    dispatch({
      type: 'city',
      value: {
        value: suggestion.locality,
        error: rules['city'](suggestion.locality)
      }
    });
    toggleStreetSuggestions(false);
  };

  const selectZipCodeSuggestion = (suggestion: SuggestedAddress) => {
    dispatch({
      type: 'zipCode',
      value: {
        value: suggestion.postalcode,
        error: rules['zipCode'](suggestion.postalcode)
      }
    });
    dispatch({
      type: 'city',
      value: {
        value: suggestion.locality,
        error: rules['city'](suggestion.locality)
      }
    });
    toggleZipCodeSuggestions(false);
  };

  return (
    <div className={styles.address}>
      <div className={styles.inputCol}>
        <Input
          type="text"
          change={inputChange(dispatch, 'name')}
          value={state.name.value}
          rule={rules.name}
          placeholder={rm('customerpage_returns_address_name')}
          errorMessage={state.name.error}
        />
        <div className={styles.suggestionWrapper}>
          <Input
            type="text"
            change={e => {
              inputChange(dispatch, 'street')(e);
              toggleStreetSuggestions(true);
            }}
            value={state.street.value}
            rule={rules.street}
            placeholder={rm('checkout_address')}
            errorMessage={state.street.error}
            blur={() => {
              toggleStreetSuggestions(false);
            }}
          />
          <AddressSuggestion
            addressInput={state.street.value}
            onSelect={selectAddressSuggestion}
            visible={streetSuggestionsVisible}
            type="address"
          />
        </div>
        <div className={styles.half}>
          <div className={styles.suggestionWrapper}>
            <Input
              type="text"
              change={e => {
                inputChange(dispatch, 'zipCode')(e);
                toggleZipCodeSuggestions(true);
              }}
              value={state.zipCode.value}
              rule={rules.zipCode}
              placeholder={rm('checkout_zipcode')}
              className={styles.input}
              errorMessage={state.zipCode.error}
              blur={() => {
                toggleZipCodeSuggestions(false);
              }}
              inputMode="numeric"
            />
            <AddressSuggestion
              addressInput={state.zipCode.value}
              onSelect={selectZipCodeSuggestion}
              visible={zipCodeSuggestionsVisible}
              type="zipCode"
            />
          </div>
          <Input
            type="text"
            change={inputChange(dispatch, 'city')}
            value={state.city.value}
            rule={rules.city}
            placeholder={rm('checkout_city')}
            className={styles.input}
            errorMessage={state.city.error}
          />
        </div>
      </div>
      <div className={styles.inputCol}>
        <Input
          type="tel"
          change={inputChange(dispatch, 'phone')}
          value={state.phone.value}
          rule={rules.phone}
          placeholder={rm('checkout_cellphone')}
          errorMessage={state.phone.error}
          inputMode="tel"
        />
        <Input
          type="email"
          change={inputChange(dispatch, 'email')}
          value={state.email.value}
          rule={rules.email}
          placeholder={rm('checkout_email_rd3')}
          errorMessage={state.email.error}
        />
        <div className={styles.buttonContainer}>
          <Button
            appearance="whitePillPrimary"
            className={styles.addressButton}
            onClick={toggleInternal}
          >
            {rm('customerpage_form_save')}
          </Button>
          <Button appearance="whitePill" className={styles.addressButton} onClick={reset}>
            {rm('customerpage_returnquantity_modal_cancel')}
          </Button>
        </div>
      </div>
    </div>
  );
};

const DisplayAddress = ({ state, toggle }: DisplayAddressProps) => {
  return (
    <div className={styles.address}>
      <div>
        <strong>{rm('checkout_address')}</strong>
        <br />
        {state.name.value}
        <br />
        {state.street.value}
        <br />
        {state.zipCode.value} {state.city.value}
      </div>
      <div>
        <strong>{rm('checkout_cellphone')}</strong>
        <br />
        {state.phone.value}
        <br />
        <strong>{rm('sip_email')}</strong>
        <br />
        {state.email.value}
      </div>
      <Button appearance="whitePill" className={styles.changeBtn} onClick={toggle}>
        {rm('customerpage_form_change_address')}
      </Button>
    </div>
  );
};

export const Address = ({
  address,
  email,
  onChange,
  changeMode,
  toggleChange,
  validationError
}: OwnProps) => {
  const [state, dispatch] = useReducer(ownReducer, initialState);

  useEffect(() => {
    dispatch({ type: 'name', value: { value: address?.name ?? '', error: '' } });
    dispatch({ type: 'street', value: { value: address?.street ?? '', error: '' } });
    dispatch({ type: 'zipCode', value: { value: address?.zipCode ?? '', error: '' } });
    dispatch({ type: 'city', value: { value: address?.city ?? '', error: '' } });
    dispatch({ type: 'phone', value: { value: address?.phone ?? '', error: '' } });
    dispatch({ type: 'email', value: { value: email ?? '', error: '' } });
  }, [address, email]);

  if (address === undefined) {
    return NO_ELEMENT;
  }

  const reset = () => {
    dispatch({ type: 'name', value: { value: address?.name ?? '', error: '' } });
    dispatch({ type: 'street', value: { value: address?.street ?? '', error: '' } });
    dispatch({ type: 'zipCode', value: { value: address?.zipCode ?? '', error: '' } });
    dispatch({ type: 'city', value: { value: address?.city ?? '', error: '' } });
    dispatch({ type: 'phone', value: { value: address?.phone ?? '', error: '' } });
    dispatch({ type: 'email', value: { value: email ?? '', error: '' } });
    toggleChange();
  };

  return changeMode ? (
    <ChangeAddress
      state={state}
      toggle={toggleChange}
      dispatch={dispatch}
      reset={reset}
      onChange={onChange}
      validationError={validationError}
    />
  ) : (
    <DisplayAddress state={state} toggle={toggleChange} />
  );
};
